// apps/web/src/lib/source-span.ts
//
// LAT-1555 owned the source-span grammar here; LAT-1840 moved the grammar
// itself down into `@atmina/kb-view/source-span` and left this module as its
// in-app door.
//
// Why the move: ADR 0058 §10 requires a published page to preserve `#L120` /
// `#L120-L126` fragments through link rewriting, and the rewriter
// (`packages/kb-view/src/reader-links.ts`) is in the package. A package cannot
// depend on an app, so honouring span fragments on publication would otherwise
// have needed a second copy of the grammar — the silent-divergence failure mode
// ADR 0049 §1 names, and refuses, for the `/docs` slug grammar.
//
// Why this file survives the move: every call site in `apps/web` keeps
// importing `@/lib/source-span`, so the relocation changed no consumer and no
// behaviour. `apps/web/test/lib/source-span.test.ts` runs unchanged, against
// the moved implementation, through these re-exports.
//
// `buildSourceLineWindow` stays here on purpose. It is rendering, not grammar:
// it clamps an already-parsed range against one document's text and projects
// the bounded, context-padded window the in-app source view draws. Nothing on
// the publication path needs it, and the package renders no such view.

import {
  parseSourceLineRangeFragment,
  parseSourceLineRangeParam,
  serializeSourceLineRange,
  splitSourceLines,
  type SourceLineRange,
} from "@atmina/kb-view/source-span";

export {
  parseSourceLineRangeFragment,
  parseSourceLineRangeParam,
  serializeSourceLineRange,
  splitSourceLines,
  type SourceLineRange,
};

export type SourceLineWindow =
  | {
      status: "ready";
      requested: SourceLineRange;
      visible: SourceLineRange;
      totalLines: number;
      lines: Array<{ number: number; text: string; selected: boolean }>;
    }
  | {
      status: "out-of-range";
      requested: SourceLineRange;
      totalLines: number;
    };

export function buildSourceLineWindow(
  sourceText: string,
  requested: SourceLineRange,
  contextLines = 20,
): SourceLineWindow {
  // LAT-1847 — the line split reads `@atmina/kb-view/source-span`'s ONE rule
  // rather than restating it. Same bytes in, same lines out; the attestation
  // read path counts lines the same way, which is what makes A16's
  // "requested range" mean one thing on both surfaces.
  const sourceLines = splitSourceLines(sourceText);
  const totalLines = sourceLines.length;

  if (requested.startLine > totalLines) {
    return { status: "out-of-range", requested, totalLines };
  }

  const visible = {
    startLine: requested.startLine,
    endLine: Math.min(requested.endLine, totalLines),
  };
  const windowStartLine = Math.max(1, visible.startLine - contextLines);
  const windowEndLine = Math.min(totalLines, visible.endLine + contextLines);
  const lines = sourceLines
    .slice(windowStartLine - 1, windowEndLine)
    .map((text, index) => {
      const number = windowStartLine + index;
      return {
        number,
        text,
        selected: number >= visible.startLine && number <= visible.endLine,
      };
    });

  return { status: "ready", requested, visible, totalLines, lines };
}
